import type { ComponentChildren, JSX } from "preact";
import { useState } from "preact/hooks";
import type { Snapshot } from "./api";
import { DetailPanel, type Target } from "./DetailPanel";
import { NewKey, NewWorkspace, RevokeKey } from "./IssueForms";
import { labelText, since, until } from "./format";
import { Overview } from "./Overview";
import { RowMenu } from "./RowMenu";
import { Button, LockIcon, Tooltip } from "./ui";

/**
 * 대시보드. 데이터 밀도가 목적이라 표는 조밀하게 두고, 화면은 목록마다 하나씩 나눈다
 * (한 페이지에 다섯 표를 쌓으면 어느 것도 넓게 못 본다).
 *
 * 컴포넌트로 옮긴 이득이 바로 여기 있다 — 예전엔 열 정의가 문자열 안의 배열이라 필드명을
 * 틀려도 런타임에 `undefined` 로 조용히 비었다. 지금은 `Snapshot` 타입에 묶여 컴파일이
 * 잡는다.
 */

interface Col<T> {
  label: string;
  num?: boolean;
  cell: (row: T) => JSX.Element | string;
}

function Table<T>({
  rows,
  cols,
  actions,
}: {
  rows: T[];
  cols: Array<Col<T>>;
  /** 행 끝의 `...` 메뉴. 목록이 읽기 전용이면 생략한다. */
  actions?: (row: T) => ComponentChildren;
}) {
  if (rows.length === 0) return <div class="empty">없음</div>;
  return (
    <div class="scroll">
      <table>
        <thead>
          <tr>
            {cols.map((c) => (
              <th key={c.label} class={c.num ? "num" : undefined}>
                {c.label}
              </th>
            ))}
            {actions ? <th class="tight" /> : null}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i}>
              {cols.map((c) => (
                <td key={c.label} class={c.num ? "num" : undefined}>
                  {c.cell(row)}
                </td>
              ))}
              {actions ? <td class="tight">{actions(row)}</td> : null}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const Faint = ({ children }: { children: ComponentChildren }) => (
  <span class="faint">{children}</span>
);
const dash = (v: string | null | undefined): JSX.Element | string =>
  v ? v : <Faint>-</Faint>;

function Section({
  title,
  action,
  children,
}: {
  title: string;
  /** 제목 줄 오른쪽 버튼 (만들기 등). h2 의 늘어나는 헤어라인이 밀어 준다. */
  action?: ComponentChildren;
  children: ComponentChildren;
}) {
  return (
    <section>
      <h2>
        {title}
        {action ? <span class="h2act">{action}</span> : null}
      </h2>
      {children}
    </section>
  );
}

const VIEWS = [
  { id: "", label: "개요" },
  { id: "sandboxes", label: "샌드박스" },
  { id: "workspaces", label: "워크스페이스" },
  { id: "keys", label: "키" },
] as const;

export function Dashboard({
  snap,
  view,
  stamp,
  error,
  canDelete,
  onRefresh,
  onLogout,
}: {
  snap: Snapshot | null;
  view: string;
  stamp: string;
  error?: string;
  canDelete: boolean;
  onRefresh: () => void;
  onLogout: () => void;
}) {
  const [target, setTarget] = useState<Target | null>(null);
  /**
   * 발급 모달. 상세 서랍(Target)과 따로 두는 이유: 서랍은 "이미 있는 것 하나"를 보는
   * 화면이고 이건 "새로 만드는" 화면이라, 같은 상태로 묶으면 열림 조건이 서로 얽힌다.
   */
  const [modal, setModal] = useState<
    { kind: "new-workspace" } | { kind: "new-key"; workspace: string } | { kind: "revoke"; keyId: string } | null
  >(null);
  const go = (id: string) => {
    location.hash = id ? `#/${id}` : "#/";
  };

  return (
    <>
      <header>
        <h1>
          <span class="dot" aria-hidden="true" />
          timely-sandbox
        </h1>
        {snap?.identity ? <span class="badge">{snap.identity}</span> : null}
        <span class="grow" />
        <span class="badge now">{stamp}</span>
        <Button onClick={onRefresh}>새로고침</Button>
        <Button onClick={onLogout}>로그아웃</Button>
      </header>
      <nav class="tabs">
        {VIEWS.map((v) => (
          <button
            key={v.id}
            class={v.id === view ? "tab on" : "tab"}
            onClick={() => go(v.id)}
          >
            {v.label}
            {snap ? <em>{count(snap, v.id)}</em> : null}
          </button>
        ))}
      </nav>
      {error ? <div class="err-line pad">{error}</div> : null}
      {!snap ? (
        <main>
          <div class="empty">읽는 중…</div>
        </main>
      ) : (
        <main>
          {view === "" ? <Overview snap={snap} onOpen={go} /> : null}

          {view === "sandboxes" ? (
            <Section title="샌드박스">
              <Table
                rows={snap.sandboxes}
                cols={[
                  { label: "id", cell: (s) => s.id },
                  { label: "workspace", cell: (s) => dash(s.workspace) },
                  {
                    label: "state",
                    cell: (s) => (
                      <span class={s.state === "claimed" ? "ok" : "dim"}>{s.state}</span>
                    ),
                  },
                  { label: "age", num: true, cell: (s) => since(s.createdAt) },
                  { label: "활동", num: true, cell: (s) => since(s.lastActivityAt) },

                  { label: "labels", cell: (s) => labelText(s.labels) },

                ]}
                actions={(s) => (
                  <RowMenu
                    items={[
                      { label: "상세", onClick: () => setTarget({ kind: "sandbox", item: s }) },
                      {
                        label: "삭제",
                        danger: true,
                        // 삭제도 패널을 연다 — 목록에서 바로 지우면 무엇을 지우는지(볼륨
                        // 포함 여부) 보여줄 자리가 없고, 잘못된 행을 지우기 쉽다.
                        onClick: () => setTarget({ kind: "sandbox", item: s }),
                      },
                    ]}
                  />
                )}
              />
            </Section>
          ) : null}

{view === "workspaces" ? (
            <Section
              title="워크스페이스"
              action={<Button small onClick={() => setModal({ kind: "new-workspace" })}>워크스페이스 만들기</Button>}
            >
              <Table
                rows={snap.workspaces}
                cols={[
                  { label: "name", cell: (w) => w.name },
                  {
                    label: "sandbox 한도",
                    num: true,
                    cell: (w) =>
                      w.maxSandboxes === null ? <Faint>무제한</Faint> : String(w.maxSandboxes),
                  },
                  {
                    label: "volume 한도",
                    num: true,
                    cell: (w) =>
                      w.maxVolumeGi === null ? <Faint>무제한</Faint> : `${w.maxVolumeGi}Gi`,
                  },
                  { label: "note", cell: (w) => dash(w.note) },
                  { label: "age", num: true, cell: (w) => since(w.createdAt) },
                ]}
                actions={(w) => (
                  <RowMenu
                    items={[
                      { label: "키 발급", onClick: () => setModal({ kind: "new-key", workspace: w.name }) },
                    ]}
                  />
                )}
              />
            </Section>
          ) : null}

          {view === "keys" ? (
            <Section title="키">
              <Table
                rows={snap.keys}
                cols={[
                  { label: "keyId", cell: (k) => k.keyId },
                  {
                    label: "종류",
                    cell: (k) => (
                      <>
                        {k.kind === "admin" ? (
                          k.readOnly ? (
                            <Faint>admin (읽기전용)</Faint>
                          ) : (
                            <span class="warn">admin</span>
                          )
                        ) : (
                          <span class="ok">workspace</span>
                        )}
                        {/* env 키는 배포 설정에 박힌 고정값이라 폐기 대상이 아니다. */}
                        {k.source === "env" ? <Faint> · 고정</Faint> : null}
                      </>
                    ),
                  },
                  { label: "workspace", cell: (k) => dash(k.workspace) },
                  { label: "note", cell: (k) => dash(k.note) },
                  { label: "발급자", cell: (k) => dash(k.createdBy) },
                  {
                    label: "만료",
                    cell: (k) =>
                      k.expiresAt === null ? (
                        // 무기한 키는 경고다 — 폐기를 사람이 기억해야 하는 유일한 종류다.
                        <span class="warn">무기한</span>
                      ) : k.expired ? (
                        <span class="bad">만료됨</span>
                      ) : (
                        <>in {until(k.expiresAt)}</>
                      ),
                  },
                ]}
                actions={(k) =>
                  k.source === "env" ? (
                    // 폐기 버튼을 아예 만들지 않는다. 누를 수 없는 버튼을 보여주면 "왜
                    // 안 되나"를 매번 다시 묻게 되므로, 잠김 표시와 이유만 남긴다.
                    <span class="locked">
                      <LockIcon />
                      <Tooltip
                        text="배포 설정에 고정된 키라 콘솔에서 폐기할 수 없습니다. 바꾸려면 클러스터 Secret 을 수정하고 컨트롤러를 재배포해야 합니다."
                      />
                    </span>
                  ) : (
                    <RowMenu
                      items={[
                        {
                          label: "폐기",
                          danger: true,
                          onClick: () => setModal({ kind: "revoke", keyId: k.keyId }),
                        },
                      ]}
                    />
                  )
                }
              />
            </Section>
          ) : null}
        </main>
      )}
      {modal ? (
        <>
          <div class="veil" onClick={() => setModal(null)} />
          <div class="modal" role="dialog">
            <h3 class="mtitle">
              {modal.kind === "new-workspace"
                ? "워크스페이스 만들기"
                : modal.kind === "new-key"
                  ? "소비자 키 발급"
                  : "키 폐기"}
            </h3>
            {modal.kind === "new-workspace" ? (
              <NewWorkspace
                onDone={() => {
                  setModal(null);
                  onRefresh();
                }}
              />
            ) : modal.kind === "new-key" ? (
              <NewKey
                workspace={modal.workspace}
                onDone={() => {
                  setModal(null);
                  onRefresh();
                }}
              />
            ) : (
              <RevokeKey
                keyId={modal.keyId}
                onDone={() => {
                  setModal(null);
                  onRefresh();
                }}
              />
            )}
          </div>
        </>
      ) : null}
      {target ? (
        <DetailPanel
          target={target}
          canDelete={canDelete}
          onClose={() => setTarget(null)}
          onDeleted={() => {
            setTarget(null);
            onRefresh();
          }}
        />
      ) : null}
    </>
  );
}

function count(snap: Snapshot, view: string): string {
  if (view === "sandboxes") return String(snap.sandboxes.length);
  if (view === "workspaces") return String(snap.workspaces.length);
  if (view === "keys") return String(snap.keys.length);
  return "";
}
